/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { GlobalConstants } from '../../constants/global-constants';
import { Observable, Subject, catchError, filter, first, of, switchMap, take, takeUntil } from 'rxjs';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { SystemState } from '../../models/system-state';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TooltipItem } from '../../../../../data-access/src/lib/domain/tooltip-item';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import {
  ADD_FITTING_PANEL_FRAME_FACADE_TOKEN,
  IAddFittingPanelFrameFacade,
} from '../../../../../data-access/src/lib/domain/facades-models/add-fitting-panel-frame-facade-interface';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { SysQueContextMenuComponent } from '../../../../../../shared/ui/src/lib/components/sysque-context-menu/sysque-context-menu.component';
import { ElectricalCategoryNames } from '../../../../../../shared/ui/src/lib/enums/electrical-category-names';
import { DxContextMenuComponent } from 'devextreme-angular/ui/context-menu';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SystemCategories } from '../../models/system-categories';
import { CategoryScopeComponent } from '../category-scope/category-scope.component';
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';

@Component({
  selector: 'sysque-add-fitting-panel-frame',
  templateUrl: './add-fitting-panel-frame.component.html',
  styleUrls: ['./add-fitting-panel-frame.component.scss'],
  standalone: false
})
export class AddFittingPanelFrameComponent implements OnInit, OnDestroy {
  @Input() categoryList$: Observable<TabDescription[]>;
  @Input() fittingList$: Observable<Item[]>;
  @Input() showAddFittingBtn: boolean;
  @Input() showRootPath: boolean;
  @Output() onShowInEnclosingFolder: EventEmitter<Item> = new EventEmitter();
  @Output() pasteError: EventEmitter<string> = new EventEmitter<string>();
  @Output() showDuplicateError: EventEmitter<PastedItemsInfo[]> = new EventEmitter<PastedItemsInfo[]>();
  @ViewChild(CategoryScopeComponent) categoryScope: CategoryScopeComponent;

  public isPanelSystem = true;
  public system: string = '';
  public menuItems: any[];
  public categoryMenuItems: any[];
  public isContextMenuVisible: boolean = this.system != '';
  public tooltipItems: TooltipItem[] = [];
  public selectedCategory: string;
  public addFittingState = new SysqueElectricalState();
  public systemInfo$: Observable<SystemInfo[]>;
  public fittingList: Item[] = [];
  public electricalCategoryNames = Object.values(ElectricalCategoryNames).map((value) => value.toLocaleLowerCase());
  public rightClickMenuItems = [{ text: GlobalConstants.Paste }];
  public showRightClickMenu = false;
  public selectedItems: Item[] = [];
  public categories = SystemCategories.getSystemCategories().slice(2, 7);
  public warningMsg: string = '';
  public copiedItemInfo: PastedItemsInfo = new PastedItemsInfo();
  public copiedItemList: PastedItemsInfo[] = [];
  public duplicateItemList: PastedItemsInfo[];
  public srcSelectedSystem: string;
  public srcSelectedCategory: string;
  public destSelectedSystem: string;
  public destSelectedCategory: string;
  public tempCategories: TabDescription[] = [];
  public tempSystemList: SystemInfo[] = [];
  public copiedItems: Item[] = [];
  public selectedSystemIndex: number;
  public selectedCategoryIndex: number;
  private readonly closed$ = new Subject<void>();

  constructor(
    @Inject(ADD_FITTING_PANEL_FRAME_FACADE_TOKEN) public facade: IAddFittingPanelFrameFacade,
    public copyPasteService: SysQueCopyPasteService,
    private cdr: ChangeDetectorRef,
    private sysqueStateService: SysQueStateService,
    private localStorageService: LocalStorageService
  ) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
    this.facade.updateLocalStorage(GlobalConstants.DockableKey, false);
    this.facade.updateLocalStorage(GlobalConstants.AddFittingsKey, true);

    // Set initial state for sysqueStateService
    this.sysqueStateService.setElectricalState(this.facade.getDockedState());

    // Subscribe to electricalState changes
    this.sysqueStateService.electricalState$.pipe(takeUntil(this.closed$)).subscribe((state) => {
      this.addFittingState = state;

      if (Array.isArray(this.addFittingState.fittingList)) {
        this.addFittingState.fittingList.forEach((item) => {
          item.isCopyState = false;
        });
      }

      this.updateData();
      this.tempSystemList = this.addFittingState.systemList;
    });

    this.tempSystemList = this.addFittingState.systemList;
    this.clearFittingsAndCategories();
    this.retrieveCategories(this.addFittingState.selectedSystem);
    this.updateData();
    this.updateCopyPasteValues();
  }

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  private updateData(): void {
    // Update local variables based on current state
    this.system = this.addFittingState.selectedSystem;
    this.selectedCategory = this.addFittingState.selectedCategory;
    this.fittingList = this.addFittingState.fittingList;

    const isSystemEmpty = this.system === '';
    const isCategoryElectrical = this.electricalCategoryNames.includes(this.selectedCategory.toLocaleLowerCase());

    this.menuItems = [
      { label: GlobalConstants.CreateSystem, disabled: false },
      { label: GlobalConstants.RenameSystem, disabled: isSystemEmpty },
      { label: GlobalConstants.DeleteSystem, disabled: isSystemEmpty },
    ];

    this.categoryMenuItems = [
      { label: GlobalConstants.CreateCategory, disabled: false },
      { label: GlobalConstants.RenameCategory, disabled: isCategoryElectrical },
      { label: GlobalConstants.DeleteCategory, disabled: isCategoryElectrical },
    ];

    this.emitStateChanges();
    this.cdr.markForCheck();
  }

  private emitStateChanges(): void {
    this.systemInfo$ = of(this.addFittingState.systemList);
    this.fittingList$ = of(this.addFittingState.fittingList);
    this.categoryList$ = of(this.addFittingState.categoryList);
  }

  public onValueChanged(e: any): void {
    const { value } = e;
    if (value === undefined || value === '') { 
      return;
    };

    this.clearFittingsAndCategories();
    this.sysqueStateService.updateProperty('selectedSystem', value);

    [1, 2].forEach((index) => (this.menuItems[index].disabled = this.system === ''));

    this.retrieveCategories(value as string);
    this.setDefaultCategoryAndFittingList();
  }

  private retrieveCategories(selectedSystem: string): void {
    this.categoryList$ = this.facade.notifyGetCategoryListAction(selectedSystem);
    this.categoryList$.pipe(take(1)).subscribe((categories) => {
      this.sysqueStateService.updateProperty('categoryList', categories);
      this.addFittingState.categoryList = categories;
    });
  }

  private clearFittingsAndCategories(): void {
    this.fittingList$ = of([]);
    this.fittingList = [];
    this.categoryList$ = of([]);
  }

  public updateCopyPasteValues(): void {
    this.copiedItemList = [];
    this.duplicateItemList = [];
    this.selectedItems = [];
    this.copyPasteService.setCopiedItemsName([]);
    this.copyPasteService.setCopiedItemsList([]);
    this.copyPasteService.setDuplicateItemsList([]);
    this.copyPasteService.setUniqueCopiedItemsList([]);
  }

  public setDefaultCategoryAndFittingList(): void {
    this.categoryList$
      .pipe(
        first(),
        switchMap((categories) => {
          if (categories && categories.length > 0) {
            this.selectedCategory = categories[0].tabName;
            this.sysqueStateService.updateProperty('selectedCategory', this.selectedCategory);

            const isElectricalCategory = this.electricalCategoryNames.includes(
              this.selectedCategory.toLocaleLowerCase()
            );

            [1, 2].forEach((index) => (this.categoryMenuItems[index].disabled = isElectricalCategory));
          }
          return this.facade.getFittings(this.addFittingState.selectedSystem, this.selectedCategory);
        }),
        catchError((error) => {
          console.error('Error fetching fittings:', error);
          return [];
        }),
        take(1)
      )
      .subscribe((data) => {
        if (!data) {
          this.sysqueStateService.updateProperty('fittingList', []);
          this.fittingList = [];
          this.fittingList$ = of([]);
          this.updateState();
        } else {
          const fittingList = data.map((item) => {
            return {
              ...item,
              tooltipItems: this.facade.getTooltipItemInfo(item, this.selectedCategory),
            };
          });
          this.sysqueStateService.updateProperty('fittingList', fittingList);
          this.fittingList = fittingList;
          this.fittingList$ = of(fittingList);
          this.updateState();
        }
      });
  }

  public updateState(): void {
    this.sysqueStateService.setElectricalState(this.addFittingState);
  }

  public onCategoryChanged({ value }: { value: any; event?: any }): void {
    if (value === undefined) {
      return;
    };
    setTimeout(() => {
      this.selectedCategoryIndex = this.addFittingState.categoryList.findIndex((x) => x.tabName === value);
    }, 0);
    this.fittingList = [];
    this.fittingList$ = this.facade.notifyGetFittingsListAction(this.addFittingState.selectedSystem, value);

    this.fittingList$.pipe(take(1)).subscribe({
      next: (response) => {
        this.sysqueStateService.updateProperty('fittingList', response);
        this.fittingList = response;
        this.sysqueStateService.updateProperty('selectedCategory', value);

        const isElectricalCategory = this.electricalCategoryNames.includes(value.toLocaleLowerCase());
        this.categoryMenuItems[1].disabled = isElectricalCategory;
        this.categoryMenuItems[2].disabled = isElectricalCategory;

        this.updateState();
      },
      error: (error) => console.error('Error fetching fittings list:', error),
    });
  }

  //#region Copy-Paste logic

  public onRightClick(e: MouseEvent): void {
    if (this.selectedItems.length > 0 && (e.target as Element).tagName.toLowerCase() !== 'img') {
      this.showRightClickMenu = true;
      this.copyPasteService.copiedItemsList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
        this.copiedItems.push(...data);
      });
      e.preventDefault();
    } else {
      this.showRightClickMenu = false;
    }
  }

  @ViewChildren(DxContextMenuComponent) contextMenus: QueryList<DxContextMenuComponent>;
  public onContextMenuShowing(event): void {
    this.contextMenus.forEach((contextMenu) => {
      if (contextMenu !== event.component) {
        contextMenu.visible = false;
      }
    });
  }

  public updateCopiedItemList(item: Item): void {
    this.selectedItems = [];
    this.copyPasteService.copiedItemsList$
      .pipe(
        filter((data) => data && data.length > 0),
        takeUntil(this.closed$)
      )
      .subscribe((data) => {
        this.selectedItems = data;
      });
    if (!this.selectedItems.includes(item)) {
      this.selectedItems.push(item);
    }
    this.srcSelectedSystem = this.addFittingState.selectedSystem;
    this.srcSelectedCategory = this.addFittingState.selectedCategory;
    this.copyPasteService.setCopiedItemsList(this.selectedItems);
  }

  public onContextMenuItemClick(event): void {
    if (event.itemData.text === GlobalConstants.Paste) {
      this.warningMsg = '';
      this.copiedItemInfo = new PastedItemsInfo();
      this.destSelectedSystem = this.addFittingState.selectedSystem;
      this.destSelectedCategory = this.addFittingState.selectedCategory;
      if (this.validatePasteScenario() === false) {
        this.pasteError.emit(this.warningMsg);
      } else {
        this.checkForDuplicateItems();
      }
    }
  }

  public validatePasteScenario(): boolean {
    const ifDestCategoryIsCustomContent = this.checkIfCustomContent(this.destSelectedCategory);
    const ifSrcCategoryIsCustomContent = this.checkIfCustomContent(this.srcSelectedCategory);
    if (this.destSelectedSystem === this.srcSelectedSystem) {
      this.warningMsg = GlobalConstants.BothSelectedSystemSame;
      return false;
    }
    if (ifSrcCategoryIsCustomContent !== 'true') {
      if (this.srcSelectedCategory !== this.destSelectedCategory) {
        this.warningMsg =
          GlobalConstants.FittingsBelogsTo +
          this.srcSelectedCategory +
          GlobalConstants.PleasePasteIn +
          this.srcSelectedCategory +
          "' tab.";
        return false;
      }
    } else {
      if (ifDestCategoryIsCustomContent !== 'true') {
        this.warningMsg = GlobalConstants.CustomContentFitting;
        return false;
      }
    }

    this.getSelectedItemsDetails();
    this.categories.forEach((category) => {
      if (category.text == this.copiedItemInfo.category) {
        return true;
      }
    });

    const isSrcClearance = this.addFittingState.systemList.find((x) => x.name === this.srcSelectedSystem)?.isClearance;
    const isDestClearance = this.addFittingState.systemList.find(
      (x) => x.name === this.destSelectedSystem
    )?.isClearance;

    if (isSrcClearance !== isDestClearance) {
      this.warningMsg = GlobalConstants.ClearanceSystemTypeMisMatch;
      return false;
    }
    return true;
  }

  public checkIfCustomContent(categoryName: string): string {
    const selectedCategory = this.addFittingState.categoryList.find((category) => category.tabName === categoryName);
    return selectedCategory ? selectedCategory.isCustomContent.toLowerCase() : '';
  }

  public async checkForDuplicateItems(): Promise<void> {
    let duplicateItemLists: PastedItemsInfo[] = [];
    let filteredList: PastedItemsInfo[] = [];
    let uniqueItemList: PastedItemsInfo[] = [];
    duplicateItemLists = await this.facade.checkForDuplicateItems(this.copiedItemList);
    if (duplicateItemLists.length > 0) {
      filteredList = duplicateItemLists?.filter((item) => item.isDuplicate);
      uniqueItemList = duplicateItemLists?.filter((item) => item.isDuplicate === false);
      this.copyPasteService.setUniqueCopiedItemsList(uniqueItemList);
    }
    if (filteredList.length > 0) {
      this.showDuplicateError.emit(filteredList);
    } else {
      this.onPasteEventLogic(this.copiedItemList);
    }
  }

  public onPasteEventLogic(copiedItems: PastedItemsInfo[]): void {
    this.facade.executePasteItemsAction(copiedItems);
    this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
    this.updateStatus();
    this.resetItemStyle(null, false);
  }

  public updateStatus(): void {
    this.systemInfo$ = of(this.addFittingState.systemList);
    this.categoryList$ = of(this.addFittingState.categoryList);
    this.fittingList$ = this.facade.getFittings(
      this.addFittingState.selectedSystem,
      this.addFittingState.selectedCategory
    );
    this.fittingList$.pipe(takeUntil(this.closed$)).subscribe((response) => {
      this.fittingList = response;
      this.sysqueStateService.updateProperty('fittingList', this.fittingList);
    });
    this.selectedItems = [];
    this.onCategoryChanged({
      value: this.addFittingState.selectedCategory,
      event: 'event from storage event listener',
    });
    this.updateCopyPasteValues();
  }

  public resetItemStyle(item: Item, isCancel: boolean): void {
    this.categoryScope.resetStyle(item, isCancel);
  }

  public getSelectedItemsDetails(): void {
    this.copiedItemList = [];
    this.selectedItems.forEach((item) => {
      this.copiedItemInfo = new PastedItemsInfo();
      this.copiedItemInfo.copiedItem = item;
      this.copiedItemInfo.srcSystemName = this.srcSelectedSystem;
      this.copiedItemInfo.destSystemName = this.destSelectedSystem;
      this.copiedItemInfo.category = this.destSelectedCategory;
      this.copiedItemList.push(this.copiedItemInfo);
    });
  }

  public removeItem(item: Item): void {
    this.resetItemStyle(item, true);
    let srcCopied: Item[] = [];
    this.copyPasteService.copiedItemsList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
      srcCopied = data;
    });
    const srcCopiedIndex = srcCopied.findIndex((i) => i.name === item.name);
    if (srcCopiedIndex !== -1) {
      srcCopied.splice(srcCopiedIndex, 1);
    }
    this.copyPasteService.setCopiedItemsList(srcCopied);
    this.facade.updateLocalStorage(GlobalConstants.DuplicateItemsUpdate, true);
  }

  //#endregion

  @ViewChild('systemContextMenu') systemContextMenu: SysQueContextMenuComponent;
  @ViewChild('categoryContextMenu') categoryContextMenu: SysQueContextMenuComponent;
  public closeOtherContextMenu(currentContextMenu: SysQueContextMenuComponent): void {
    // Close other context menu
    [this.systemContextMenu, this.categoryContextMenu].forEach((contextMenu) => {
      if (contextMenu !== currentContextMenu) {
        contextMenu.hideContextMenu();
      }
    });
  }

  public handleContextMenuItem(item: string) {
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.addFittingState);
    let sysquePopupConfig = new SysquePopupConfig();
    this.selectedSystemIndex = this.addFittingState.systemList.findIndex((x) => x.name === this.addFittingState.selectedSystem);
    switch (item) {
      case GlobalConstants.CreateSystem:
        createDialog(sysquePopupConfig, GlobalConstants.CREATE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.CREATE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.RenameSystem:
        createDialog(sysquePopupConfig, GlobalConstants.RENAME_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.RENAME_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.DeleteSystem:
        createDialog(sysquePopupConfig, GlobalConstants.DELETE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.DELETE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      default:
        break;
    }
  }

  public handleCategoryContextMenuItem(item: string): void {
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.addFittingState);
    let sysquePopupConfig = new SysquePopupConfig();
    switch (item) {
      case GlobalConstants.CreateCategory:
        createDialog(sysquePopupConfig, GlobalConstants.CREATE_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.CREATE_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
      case GlobalConstants.DeleteCategory:
        createDialog(sysquePopupConfig, GlobalConstants.DELETE_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.DELETE_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
      case GlobalConstants.RenameCategory:
        createDialog(sysquePopupConfig, GlobalConstants.RENAME_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.RENAME_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  public beforeunloadHandler(event): void {
    this.showAddFittingBtn = false;
    this.facade.updateLocalStorage(GlobalConstants.SYSTEM_STATE, new SystemState());
  }

  public manageModeClicked(): void {
    this.facade.saveChanges(GlobalConstants.Fitting2Manage);
  }

  public doneClicked(): void {
    this.facade.saveChanges(GlobalConstants.Fitting2Docked);
    this.facade.resetManageState();
    this.facade.resetFittingModeState();
    this.cdr.markForCheck();
  }

  public showInEnclosingFolder(item: Item): void {
    this.onShowInEnclosingFolder.emit(item);
  }

  public storageEventListener(event: StorageEvent): void {
    this.sysqueStateService.setElectricalState(this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY));
    this.addFittingState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    if (!event) return;

    switch (event.key) {
      case GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY:
        if (!localStorage.getItem('ManageModeOperations')) {
          const data = JSON.parse(event.newValue);
          this.sysqueStateService.setElectricalState(data);
          this.fittingList$ = of(this.addFittingState.fittingList);
          this.cdr.markForCheck();
        }
        break;
      case GlobalConstants.SYSTEM_CREATION_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.AddFittingsKey)) {
          this.storageEventCreateSystem();
        }
        break;
      case GlobalConstants.SYSTEM_DELETED_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.AddFittingsKey)) {
          this.storageEventDeleteSystem();
        }
        break;
      case GlobalConstants.SYSTEM_RENAMED_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.AddFittingsKey)) {
          this.storageEventRenameSystem();
        }
        break;
      case GlobalConstants.CATEGORY_CREATION_SUCCESS:
      case GlobalConstants.CATEGORY_RENAMED_SUCCESS:
      case GlobalConstants.CATEGORY_DELETED_SUCCESS:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          !localStorage.getItem('ManageModeOperations')
        ) {
          this.storageEventListenerForCategories(event.key);
        }
        break;
      case GlobalConstants.CanExecutePasteLogic:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          !localStorage.getItem('ManageModeOperations')
        ) {
          this.copyPasteService.duplicateItemList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
            this.copiedItemList = data;
          });
          this.onPasteEventLogic(this.copiedItemList);
        }
        break;
      case GlobalConstants.LINE_GRAPHICS_SUCCESS:
      case GlobalConstants.REFRESH_ADD_FITTING_DIALOG_DATA:
        this.onCategoryChanged({ value: this.selectedCategory });
        break;
    }
  }

  //#region Category operations

  /**
   * Handles storage events for category options
   * @param key - key to identify the storage event
   */
  private storageEventListenerForCategories(key: string): void {
    this.addFittingState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    // Handle category changes
    if (key === GlobalConstants.CATEGORY_RENAMED_SUCCESS) {
      this.addFittingState.selectedCategory = this.addFittingState.categoryList[this.selectedCategoryIndex].tabName;
      this.sysqueStateService.updateProperty('selectedCategory', this.addFittingState.selectedCategory);
    } else if (key === GlobalConstants.CATEGORY_DELETED_SUCCESS) {
      this.storageEventDeleteCategory();
    } else if (key === GlobalConstants.CATEGORY_CREATION_SUCCESS) {
      this.addFittingState.selectedCategory = this.addFittingState.categoryList[this.addFittingState.categoryList.length - 1].tabName;
      this.sysqueStateService.updateProperty('selectedCategory', this.addFittingState.selectedCategory);
    }

    this.selectedCategory = this.addFittingState.selectedCategory;

    // Update state
    this.onCategoryChanged({
      value: this.addFittingState.selectedCategory,
      event: 'event from storage event listener',
    });
  }

  /**
   * Handles storage event for category deletion
   */
  private storageEventDeleteCategory(): void {
    // When a category is deleted, the selected category should be the first one
    this.addFittingState.selectedCategory = this.addFittingState.categoryList[0].tabName;
  }

  //#endregion

  //#region Systems operations

  /**
   * Handle the events for Create System
   */
  private storageEventCreateSystem(): void {
    this.addFittingState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    // Select the newly created system
    this.addFittingState.selectedSystem =
      this.addFittingState.systemList[this.addFittingState.systemList.length - 1].name;
      this.sysqueStateService.updateProperty('selectedSystem', this.addFittingState.selectedSystem);
    // Update the state
    this.onValueChanged({
      value: this.addFittingState.selectedSystem,
      event: 'create',
    });
  }

  /**
   * Handle the events for Rename System
   */
  private storageEventRenameSystem(): void {
    // Get the updated state
    this.addFittingState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    // Ensure the selection remains on the renamed system
    if (this.selectedSystemIndex !== -1 && this.selectedSystemIndex < this.addFittingState.systemList.length) {
        this.addFittingState.selectedSystem = this.addFittingState.systemList[this.selectedSystemIndex].name;
    }

    // Synchronize the tempSystemList with the updated systemList
    this.tempSystemList = [...this.addFittingState.systemList];

    // Update the state and notify listeners
    this.sysqueStateService.updateProperty('selectedSystem', this.addFittingState.selectedSystem);
    this.onValueChanged({
        value: this.addFittingState.selectedSystem,
        event: 'rename',
    });
    this.cdr.markForCheck();
  }

  /**
   * Handle the events for Delete System
   */
  private storageEventDeleteSystem(): void {
    // Get the updated state
    this.addFittingState = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    const currentSystemList = this.addFittingState.systemList;

    // Check if the system list is empty after deletion
    if (currentSystemList.length === 0) {
        this.addFittingState.selectedSystem = null;
    } else {
        // Determine the system to select after deletion
        if (this.selectedSystemIndex > 0) {
            // Select the system before the deleted one
            this.addFittingState.selectedSystem = currentSystemList[this.selectedSystemIndex - 1].name;
        } else {
            // If the first item is deleted, select the new first system
            this.addFittingState.selectedSystem = currentSystemList[0].name;
        }
    }

    // Synchronize the tempSystemList with the current system list
    this.tempSystemList = [...currentSystemList];

    // Update state
    this.sysqueStateService.updateProperty('selectedSystem', this.addFittingState.selectedSystem);
    this.onValueChanged({
        value: this.addFittingState.selectedSystem,
        event: 'delete',
    });

    // Trigger change detection manually
    this.cdr.markForCheck();
}

  //#endregion
}
